import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, SlidersHorizontal, X } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { api, formatCurrency } from '../lib/api';

// Best-effort city extractor — matches backend's _norm_city intent so filter values line up.
const CITY_ALIASES = {
  powai: 'Mumbai', andheri: 'Mumbai', thane: 'Mumbai', 'navi mumbai': 'Mumbai', bombay: 'Mumbai',
  bengaluru: 'Bangalore', blr: 'Bangalore',
  'new delhi': 'Delhi', 'delhi ncr': 'Delhi', noida: 'Delhi',
  gurgaon: 'Gurgaon', gurugram: 'Gurgaon',
  calcutta: 'Kolkata', madras: 'Chennai', hyd: 'Hyderabad',
};
const STATE_WORDS = new Set(['maharashtra', 'karnataka', 'delhi', 'haryana', 'tamil nadu', 'telangana', 'west bengal', 'gujarat', 'uttar pradesh', 'rajasthan', 'kerala']);
const normCity = (raw) => {
  if (!raw) return null;
  const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    if (p.replace(/\s/g, '').match(/^\d+$/)) continue;
    if (STATE_WORDS.has(p.toLowerCase())) continue;
    const key = p.toLowerCase();
    return CITY_ALIASES[key] || p.replace(/\b\w/g, c => c.toUpperCase());
  }
  return null;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const todayISO = () => new Date().toISOString().split('T')[0];

const DEFAULT_FILTERS = {
  status: 'all',
  city: 'all',
  retainer_min: '',
  retainer_max: '',
  onboarded_from: '',
  onboarded_to: '',
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [retainers, setRetainers] = useState({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const empty = {
    name: '', contact_person: '', phone: '', email: '', company_id: '',
    client_type: 'Developer', address: '', gstin: '', pan: '', notes: '',
    client_status: 'active', onboarding_date: todayISO(),
  };
  const [form, setForm] = useState(empty);
  const navigate = useNavigate();
  const { isViewer } = useAuth();

  const fetchAll = () => {
    const params = search ? { search } : {};
    api.get('/clients', { params }).then(res => setClients(res.data));
    api.get('/clients-retainers').then(res => setRetainers(res.data || {})).catch(() => {});
  };

  useEffect(() => {
    api.get('/companies').then(res => setCompanies(res.data));
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openNew = () => {
    setForm({ ...empty, company_id: companies[0]?.id || '' });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (client) => {
    setForm({
      ...empty,
      ...client,
      onboarding_date: client.onboarding_date || '',
    });
    setEditId(client.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, onboarding_date: form.onboarding_date || null };
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post('/clients', payload);
      setShowForm(false);
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const retainerOf = (id) => retainers[id]?.monthly_retainer || 0;

  // Unique cities for filter dropdown
  const cityOptions = useMemo(() => {
    const s = new Set();
    clients.forEach(c => { const nc = normCity(c.address); if (nc) s.add(nc); });
    return Array.from(s).sort();
  }, [clients]);

  // Apply client-side filters
  const filteredClients = useMemo(() => {
    const rMin = filters.retainer_min === '' ? null : parseFloat(filters.retainer_min);
    const rMax = filters.retainer_max === '' ? null : parseFloat(filters.retainer_max);
    const fromD = filters.onboarded_from ? new Date(filters.onboarded_from) : null;
    const toD = filters.onboarded_to ? new Date(filters.onboarded_to) : null;
    return clients.filter(c => {
      if (filters.status !== 'all' && (c.client_status || 'active') !== filters.status) return false;
      if (filters.city !== 'all') {
        const nc = normCity(c.address);
        if (nc !== filters.city) return false;
      }
      const rAmt = retainerOf(c.id);
      if (rMin !== null && rAmt < rMin) return false;
      if (rMax !== null && rAmt > rMax) return false;
      if (fromD || toD) {
        if (!c.onboarding_date) return false;
        const od = new Date(c.onboarding_date);
        if (fromD && od < fromD) return false;
        if (toD && od > toD) return false;
      }
      return true;
    });
  }, [clients, retainers, filters]);

  const visibleIds = useMemo(() => filteredClients.map(c => c.id), [filteredClients]);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  };

  const handleDeleteOne = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    try {
      setBusy(true);
      await api.delete(`/clients/${c.id}`);
      setSelected(prev => { const n = new Set(prev); n.delete(c.id); return n; });
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected client${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      setBusy(true);
      const res = await api.post('/clients/bulk-delete', { client_ids: ids });
      setSelected(new Set());
      fetchAll();
      alert(`${res.data.deleted} client(s) deleted.`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";
  const thCls = "font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left";
  const companyName = (id) => companies.find(c => c.id === id)?.name || '—';

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.status !== 'all') n++;
    if (filters.city !== 'all') n++;
    if (filters.retainer_min !== '' || filters.retainer_max !== '') n++;
    if (filters.onboarded_from || filters.onboarded_to) n++;
    return n;
  }, [filters]);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const actionBtn = !isViewer && (
    <div className="flex items-center gap-3">
      {selected.size > 0 && (
        <button
          data-testid="bulk-delete-clients-btn"
          onClick={handleBulkDelete}
          disabled={busy}
          className="bg-maroon/10 text-maroon border border-maroon/30 hover:bg-maroon hover:text-white px-4 py-3 font-heading text-sm flex items-center transition-colors disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Delete {selected.size} selected
        </button>
      )}
      <button data-testid="new-client-button" onClick={openNew} className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-lg flex items-center">
        <Plus className="w-5 h-5 mr-2" strokeWidth={1} />New Client
      </button>
    </div>
  );

  return (
    <Layout title="Clients" action={actionBtn}>
      <div className="bg-surface border border-border p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" strokeWidth={1} />
            <input
              data-testid="client-search"
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent border border-border focus:border-maroon outline-none pl-10 pr-3 py-2 font-body text-sm"
            />
          </div>
          <button
            type="button"
            data-testid="toggle-filters-btn"
            onClick={() => setShowFilters(s => !s)}
            className={`flex items-center gap-2 px-4 py-2 font-body text-sm uppercase tracking-widest border transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'bg-maroon text-white border-maroon'
                : 'bg-transparent text-text-secondary border-border hover:border-maroon hover:text-maroon'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
            Filters
            {activeFilterCount > 0 && (
              <span data-testid="filter-count-badge" className="ml-1 px-2 py-0.5 bg-white/20 text-xs tabular-nums">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div data-testid="filter-panel" className="mb-6 border border-border bg-cream p-5 fade-up">
            <div className="flex items-center justify-between mb-4">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon">— Filters</p>
              <div className="flex items-center gap-3">
                <span className="font-body text-xs text-text-secondary">
                  Showing <span className="tabular-nums text-text-primary">{filteredClients.length}</span> of {clients.length}
                </span>
                {activeFilterCount > 0 && (
                  <button
                    data-testid="clear-filters-btn"
                    onClick={clearFilters}
                    className="flex items-center gap-1 font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover"
                  >
                    <X className="w-3 h-3" strokeWidth={1.5} />
                    Clear all
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>Status</label>
                <select
                  data-testid="filter-status"
                  value={filters.status}
                  onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                  className={inputCls}
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>City</label>
                <select
                  data-testid="filter-city"
                  value={filters.city}
                  onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}
                  className={inputCls}
                >
                  <option value="all">All cities</option>
                  {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Retainer range (₹/mo)</label>
                <div className="flex gap-2">
                  <input
                    data-testid="filter-retainer-min"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="Min"
                    value={filters.retainer_min}
                    onChange={(e) => setFilters(f => ({ ...f, retainer_min: e.target.value }))}
                    className={inputCls}
                  />
                  <input
                    data-testid="filter-retainer-max"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="Max"
                    value={filters.retainer_max}
                    onChange={(e) => setFilters(f => ({ ...f, retainer_max: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Onboarded between</label>
                <div className="flex gap-2">
                  <input
                    data-testid="filter-onboarded-from"
                    type="date"
                    value={filters.onboarded_from}
                    onChange={(e) => setFilters(f => ({ ...f, onboarded_from: e.target.value }))}
                    className={inputCls}
                  />
                  <input
                    data-testid="filter-onboarded-to"
                    type="date"
                    value={filters.onboarded_to}
                    onChange={(e) => setFilters(f => ({ ...f, onboarded_to: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr>
                {!isViewer && (
                  <th className={`${thCls} w-10`}>
                    <input
                      data-testid="select-all-clients"
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="accent-maroon w-4 h-4 cursor-pointer"
                    />
                  </th>
                )}
                <th className={thCls}>Name</th>
                <th className={thCls}>Contact</th>
                <th className={thCls}>Type</th>
                <th className={thCls}>Status</th>
                <th className={thCls}>Company</th>
                <th className={thCls}>Onboarded</th>
                <th className={`${thCls} text-right`}>Retainer / mo</th>
                <th className={`${thCls} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map(c => {
                const rid = `client-row-${c.name.replace(/\s/g, '-')}`;
                const isSel = selected.has(c.id);
                const retAmt = retainerOf(c.id);
                const tplCount = retainers[c.id]?.template_count || 0;
                return (
                  <tr key={c.id} data-testid={rid} className={`border-b border-border hover:bg-cream transition-colors ${isSel ? 'bg-cream' : ''}`}>
                    {!isViewer && (
                      <td className="py-4">
                        <input
                          data-testid={`select-client-${c.id.slice(0,8)}`}
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleOne(c.id)}
                          className="accent-maroon w-4 h-4 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="py-4 font-body text-sm text-text-primary cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>{c.name}</td>
                    <td className="py-4 font-body text-sm text-text-secondary cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>{c.contact_person} · {c.phone}</td>
                    <td className="py-4 font-body text-sm text-text-secondary cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>{c.client_type}</td>
                    <td className="py-4 cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>
                      {(() => {
                        const s = c.client_status || 'active';
                        const styles = {
                          active: 'bg-success/10 text-success border-success/30',
                          on_hold: 'bg-gold/15 text-gold border-gold/40',
                          inactive: 'bg-maroon/10 text-maroon border-maroon/30',
                        };
                        const labels = { active: 'Active', on_hold: 'On Hold', inactive: 'Inactive' };
                        return <span className={`px-2 py-1 text-xs uppercase tracking-widest font-body border ${styles[s]}`}>{labels[s]}</span>;
                      })()}
                    </td>
                    <td className="py-4 font-body text-sm text-text-secondary cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>{companyName(c.company_id)}</td>
                    <td
                      className="py-4 font-body text-sm text-text-secondary cursor-pointer tabular-nums"
                      onClick={() => navigate(`/clients/${c.id}`)}
                      data-testid={`client-onboarded-${c.id.slice(0,8)}`}
                    >
                      {fmtDate(c.onboarding_date)}
                    </td>
                    <td className="py-4 font-body text-sm text-right tabular-nums cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-retainer-${c.id.slice(0,8)}`}>
                      {retAmt > 0 ? (
                        <span className="text-text-primary">
                          {formatCurrency(retAmt)}
                          {tplCount > 1 && <span className="text-text-secondary text-xs ml-1">×{tplCount}</span>}
                        </span>
                      ) : (
                        <span className="text-text-secondary">—</span>
                      )}
                    </td>
                    <td className="py-4 text-right space-x-3 whitespace-nowrap">
                      <button data-testid={`view-client-${c.name.replace(/\s/g, '-')}`} onClick={() => navigate(`/clients/${c.id}`)} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">View</button>
                      {!isViewer && (
                        <>
                          <button onClick={() => openEdit(c)} className="font-body text-xs uppercase tracking-widest text-text-secondary hover:text-maroon">Edit</button>
                          <button
                            data-testid={`delete-client-${c.id.slice(0,8)}`}
                            onClick={() => handleDeleteOne(c)}
                            disabled={busy}
                            className="font-body text-xs uppercase tracking-widest text-text-secondary hover:text-maroon disabled:opacity-50"
                            title="Delete client"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan={isViewer ? 8 : 9} className="py-8 text-center text-text-secondary font-body text-sm" data-testid="clients-empty-state">
                    {clients.length === 0 ? 'No clients found' : 'No clients match the current filters'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleSubmit} className="bg-cream border border-border max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-heading text-2xl font-light mb-6">{editId ? 'Edit' : 'New'} Client</h2>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Name</label><input data-testid="client-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Contact Person</label><input value={form.contact_person || ''} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Phone</label><input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Email</label><input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Company</label><select data-testid="client-form-company" required value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className={inputCls}><option value="">Select</option>{companies.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}</select></div>
              <div><label className={labelCls}>Type</label><select value={form.client_type || 'Developer'} onChange={(e) => setForm({ ...form, client_type: e.target.value })} className={inputCls}>{['Developer', 'Channel Partner', 'Mandate Partner', 'Investor', 'Agency', 'Other'].map(t => <option key={t} value={t}>{t}</option>)}</select></div>
              <div><label className={labelCls}>Status</label><select data-testid="client-form-status" value={form.client_status || 'active'} onChange={(e) => setForm({ ...form, client_status: e.target.value })} className={inputCls}>{[{v:'active',l:'Active'},{v:'on_hold',l:'On Hold'},{v:'inactive',l:'Inactive'}].map(t => <option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
              <div>
                <label className={labelCls}>Onboarding Date</label>
                <input
                  data-testid="client-form-onboarding"
                  type="date"
                  value={form.onboarding_date || ''}
                  onChange={(e) => setForm({ ...form, onboarding_date: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="col-span-2"><label className={labelCls}>Address</label><input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>GSTIN</label><input value={form.gstin || ''} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>PAN</label><input value={form.pan || ''} onChange={(e) => setForm({ ...form, pan: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Notes</label><textarea rows={3} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button type="button" onClick={() => setShowForm(false)} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading">Cancel</button>
              <button data-testid="save-client-button" type="submit" className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading">{editId ? 'Update' : 'Create'}</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

export default Clients;
